/*
 *  Copyright (c) 2018-present, Evgeny Nadymov
 *
 * This source code is licensed under the GPL v.3.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import Popover from '@mui/material/Popover';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button,
  Grid, 
  Select, 
  MenuItem, 
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Stepper,
  Step,
  StepLabel,
  Alert,
  useTheme,
  styled
} from '@mui/material';
import { 
  Check, 
  ArrowForward, 
  People, 
  Shield, 
  AccessTime,
  AttachMoney,
  Warning,
  Description
} from '@mui/icons-material';
import { SOLANA_COLORS } from '../../Theme/SolanaTheme';

// Custom styled components
const GradientBox = styled(Box)(({ theme }) => ({
  background: `linear-gradient(90deg, ${SOLANA_COLORS.PURPLE}, ${SOLANA_COLORS.GREEN})`,
  borderRadius: '50%',
  width: 18,
  height: 18,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(0.5),
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(90deg, ${SOLANA_COLORS.PURPLE}, ${SOLANA_COLORS.GREEN})`,
  color: theme.palette.common.white,
  '&:hover': {
    background: `linear-gradient(90deg, ${SOLANA_COLORS.PURPLE}, ${SOLANA_COLORS.GREEN})`,
    opacity: 0.9,
  },
}));

const SectionTitle = ({ icon, title }) => {
  return (
    <Box display="flex" alignItems="center" mb={0.5}>
      <GradientBox>
        {icon}
      </GradientBox>
      <Typography variant="caption" fontWeight="medium" color="primary">
        {title}
      </Typography>
    </Box>
  );
};

const StatusBox = ({ label, value }) => {
  const theme = useTheme();
  
  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: 1, 
        bgcolor: theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" color="primary" fontFamily="monospace" fontWeight="medium">
        {value}
      </Typography>
    </Paper>
  );
};

function EscrowContract() {
  const theme = useTheme();
  const [step, setStep] = useState(0);
  const [contract, setContract] = useState({
    participant1: { deposit: '' },
    participant2: { deposit: '' },
    judgeSelection: 'random',
    specificJudge: '',
    timeframe: 7,
    agreementText: '',
    itemPrice: ''
  });

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setContract({
        ...contract,
        [parent]: {
          ...contract[parent],
          [child]: value
        }
      });
    } else {
      setContract({
        ...contract,
        [field]: value
      });
    }
  };

  const calculateJudgeFee = () => {
    const p1 = parseFloat(contract.participant1.deposit) || 0;
    const p2 = parseFloat(contract.participant2.deposit) || 0;
    const totalDeposit = p1 + p2;
    return (totalDeposit * 0.05).toFixed(2);
  };

  const calculateTotalAmount = () => {
    const p1 = parseFloat(contract.participant1.deposit) || 0;
    const p2 = parseFloat(contract.participant2.deposit) || 0;
    return (p1 + p2).toFixed(2);
  };

  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  const renderStep = () => {
    switch(step) {
      case 0:
        return (
          <Box>
            {/* Item Price */}
            <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
              <SectionTitle icon={<AttachMoney sx={{ fontSize: 12, color: 'white' }} />} title="PRICE" />
              <TextField
                fullWidth
                size="small"
                placeholder="0.00"
                value={contract.itemPrice}
                onChange={(e) => handleChange('itemPrice', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: <InputAdornment position="end">USD</InputAdornment>,
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    bgcolor: theme.palette.background.default,
                    fontSize: '0.75rem'
                  }
                }}
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Paper>
            
            <Grid container spacing={1} sx={{ mb: 1 }}>
              {/* P1 Deposit */}
              <Grid item xs={6}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <SectionTitle icon={<People sx={{ fontSize: 12, color: 'white' }} />} title="P1 DEPOSIT" />
                    <Typography variant="caption" color="text.secondary">(Chat)</Typography>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="0.00"
                    value={contract.participant1.deposit}
                    onChange={(e) => handleChange('participant1.deposit', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="caption" color="primary" fontFamily="monospace">
                            SOL
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        bgcolor: theme.palette.background.default,
                        fontSize: '0.75rem'
                      }
                    }}
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Paper>
              </Grid>
              
              {/* P2 Deposit */}
              <Grid item xs={6}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <SectionTitle icon={<People sx={{ fontSize: 12, color: 'white' }} />} title="P2 DEPOSIT" />
                    <Typography variant="caption" color="text.secondary">(Chat)</Typography>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="0.00"
                    value={contract.participant2.deposit}
                    onChange={(e) => handleChange('participant2.deposit', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="caption" color="primary" fontFamily="monospace">
                            SOL
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        bgcolor: theme.palette.background.default,
                        fontSize: '0.75rem'
                      }
                    }}
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Paper>
              </Grid>
            </Grid>
            
            <Grid container spacing={1} sx={{ mb: 1 }}>
              {/* Judge Selection */}
              <Grid item xs={6}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <SectionTitle icon={<Shield sx={{ fontSize: 12, color: 'white' }} />} title="JUDGE" />
                  </Box>
                  <ToggleButtonGroup
                    value={contract.judgeSelection}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        handleChange('judgeSelection', newValue);
                      }
                    }}
                    size="small"
                    fullWidth
                    sx={{ mb: contract.judgeSelection === 'specific' ? 1 : 0 }}
                  >
                    <ToggleButton value="random" sx={{ py: 0.25 }}>
                      <Typography variant="caption">Random</Typography>
                    </ToggleButton>
                    <ToggleButton value="specific" sx={{ py: 0.25 }}>
                      <Typography variant="caption">Specific</Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                  
                  {contract.judgeSelection === 'specific' && (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Judge ID"
                      value={contract.specificJudge}
                      onChange={(e) => handleChange('specificJudge', e.target.value)}
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          bgcolor: theme.palette.background.default,
                          fontSize: '0.75rem'
                        }
                      }}
                    />
                  )}
                </Paper>
              </Grid>
              
              {/* Timeframe */}
              <Grid item xs={6}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <SectionTitle icon={<AccessTime sx={{ fontSize: 12, color: 'white' }} />} title="TIME" />
                  <Select
                    fullWidth
                    value={contract.timeframe}
                    onChange={(e) => handleChange('timeframe', e.target.value)}
                    size="small"
                    sx={{ 
                      bgcolor: theme.palette.background.default,
                      '& .MuiSelect-select': {
                        fontSize: '0.75rem',
                        py: 0.75
                      }
                    }}
                  >
                    <MenuItem value={3}>3 days</MenuItem>
                    <MenuItem value={5}>5 days</MenuItem>
                    <MenuItem value={7}>7 days</MenuItem>
                    <MenuItem value={14}>14 days</MenuItem>
                    <MenuItem value={30}>30 days</MenuItem>
                  </Select>
                </Paper>
              </Grid>
            </Grid>
            
            {/* Fee Summary */}
            <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
              <SectionTitle icon={<AttachMoney sx={{ fontSize: 12, color: 'white' }} />} title="FEES" />
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <StatusBox label="Total" value={`${calculateTotalAmount()} SOL`} />
                </Grid>
                <Grid item xs={4}>
                  <StatusBox label="Fee" value={`${calculateJudgeFee()} SOL`} />
                </Grid>
                <Grid item xs={4}>
                  <StatusBox label="Stake" value={`${(parseFloat(calculateTotalAmount()) - parseFloat(calculateJudgeFee())).toFixed(2)} SOL`} />
                </Grid>
              </Grid>
            </Paper>
            
            {/* Context */}
            <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <SectionTitle icon={<Description sx={{ fontSize: 12, color: 'white' }} />} title="CONTEXT" />
                <GradientButton 
                  onClick={nextStep}
                  size="small"
                  endIcon={<ArrowForward fontSize="small" />}
                  sx={{ py: 0.25, px: 1, typography: 'caption' }}
                >
                  Review
                </GradientButton>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Describe conversation context..."
                value={contract.agreementText}
                onChange={(e) => handleChange('agreementText', e.target.value)}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    bgcolor: theme.palette.background.default,
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Paper>
          </Box>
        );
      
      case 1:
        return (
          <Box>
            <Box display="flex" flexDirection="column" alignItems="center" mb={1}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(90deg, ${SOLANA_COLORS.PURPLE}, ${SOLANA_COLORS.GREEN})`,
                  mb: 0.5
                }}
              >
                <Check sx={{ fontSize: 16, color: 'white' }} />
              </Box>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Contract Summary
              </Typography>
            </Box>
            
            {/* Item and Deposits */}
            <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
              <SectionTitle icon={<AttachMoney sx={{ fontSize: 12, color: 'white' }} />} title="DETAILS" />
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={4}>
                  <StatusBox label="Price" value={`$${contract.itemPrice}`} />
                </Grid>
                <Grid item xs={4}>
                  <StatusBox label="P1" value={`${contract.participant1.deposit} SOL`} />
                </Grid>
                <Grid item xs={4}>
                  <StatusBox label="P2" value={`${contract.participant2.deposit} SOL`} />
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <StatusBox label="Total" value={`${calculateTotalAmount()} SOL`} />
                </Grid>
                <Grid item xs={4}>
                  <StatusBox label="Fee (5%)" value={`${calculateJudgeFee()} SOL`} />
                </Grid>
                <Grid item xs={4}>
                  <StatusBox label="At Stake" value={`${(parseFloat(calculateTotalAmount()) - parseFloat(calculateJudgeFee())).toFixed(2)} SOL`} />
                </Grid>
              </Grid>
            </Paper>

            {/* Resolution */}
            <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
              <SectionTitle icon={<Shield sx={{ fontSize: 12, color: 'white' }} />} title="RESOLUTION" />
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 1, bgcolor: theme.palette.background.default }}>
                    <Typography variant="caption" color="text.secondary">Judge</Typography>
                    <Typography variant="body2" color="text.primary">
                      {contract.judgeSelection === 'random' 
                        ? 'Random Judge' 
                        : (contract.specificJudge || 'Not specified')}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 1, bgcolor: theme.palette.background.default }}>
                    <Typography variant="caption" color="text.secondary">Timeframe</Typography>
                    <Typography variant="body2" color="text.primary">
                      {contract.timeframe} days
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>

            {/* Context */}
            <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
              <SectionTitle icon={<Description sx={{ fontSize: 12, color: 'white' }} />} title="CONTEXT" />
              <Paper variant="outlined" sx={{ p: 1, bgcolor: theme.palette.background.default }}>
                <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.75rem' }}>
                  {contract.agreementText || "No description provided"}
                </Typography>
              </Paper>
            </Paper>

            {/* Notice */}
            <Alert 
              icon={<Warning fontSize="small" />} 
              severity="warning"
              variant="outlined"
              sx={{ 
                mb: 1, 
                '& .MuiAlert-message': { 
                  fontSize: '0.75rem',
                  py: 0
                }
              }}
            >
              Full conversation sent to judge. Judge fee: 5%. Decision final.
            </Alert>

            <Box display="flex" justifyContent="space-between" mt={1}>
              <Button 
                variant="outlined"
                size="small"
                onClick={prevStep}
              >
                Back
              </Button>
              <GradientButton 
                onClick={nextStep}
                size="small"
                endIcon={<ArrowForward fontSize="small" />}
              >
                Create
              </GradientButton>
            </Box>
          </Box>
        );
      
      case 2:
        return (
          <Box display="flex" flexDirection="column" alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(90deg, ${theme.palette.success.main}, ${SOLANA_COLORS.GREEN})`,
                mb: 1
              }}
            >
              <Check sx={{ fontSize: 20, color: 'white' }} />
            </Box>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Contract Created!
            </Typography>
            
            <Paper variant="outlined" sx={{ p: 1, width: '100%', mb: 2 }}>
              <Typography variant="caption" color="text.secondary" paragraph>
                Contract ID:
              </Typography>
              <Paper variant="outlined" sx={{ p: 1, bgcolor: theme.palette.background.default, mb: 1 }}>
                <Typography variant="body2" color="primary" fontFamily="monospace">
                  ESC-{Math.random().toString(36).substring(2, 6).toUpperCase()}
                </Typography>
              </Paper>
              
              {/* <Typography variant="caption" color="text.secondary">
                Next steps:
              </Typography> */}
              <Box component="ol" sx={{ pl: 2, m: 0, fontSize: '0.75rem' }}>
                Decision in {contract.timeframe} days
              </Box>
            </Paper>

            <GradientButton 
              onClick={() => setStep(0)}
              size="small"
            >
              New Contract
            </GradientButton>
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 520,
        mx: 'auto',
        p: 1.5,
        borderRadius: 2,
        bgcolor: theme.palette.mode === 'dark' ? '#121212' : '#f5f5f5',
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box display="flex" alignItems="center">
          <Box 
            sx={{ 
              width: 10, 
              height: 10, 
              mr: 0.5, 
              borderRadius: 0.5,
              background: `linear-gradient(90deg, ${SOLANA_COLORS.PURPLE}, ${SOLANA_COLORS.GREEN})` 
            }} 
          />
          <Typography variant="subtitle2">
            <Box component="span" sx={{ 
              background: `linear-gradient(90deg, ${SOLANA_COLORS.PURPLE}, ${SOLANA_COLORS.GREEN})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Escrow
            </Box> Contract
          </Typography>
        </Box>
        <Paper 
          variant="outlined" 
          sx={{ 
            px: 0.75, 
            py: 0.25,
            borderRadius: 1
          }}
        >
          <Typography variant="caption" color="text.secondary">
            5% judge fee
          </Typography>
        </Paper>
      </Box>

      {step < 2 && (
        <Box display="flex" justifyContent="center" mb={1}>
          <Stepper
            activeStep={step}
            alternativeLabel
            sx={{
              width: '120px',
              '& .MuiStepLabel-root': {
                padding: 0
              },
              '& .MuiStepLabel-iconContainer': {
                padding: 0,
                '& .MuiSvgIcon-root': {
                  fontSize: 16
                }
              },
              '& .MuiStepConnector-line': {
                minHeight: 2
              }
            }}
          >
            <Step>
              <StepLabel />
            </Step>
            <Step>
              <StepLabel />
            </Step>
          </Stepper>
        </Box>
      )}

      {renderStep()}
    </Box>
  );
}

function SolanaContractDialog(props) {
  const { open, onClose, anchorEl } = props;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      elevation={3}
      sx={{
        '& .MuiPopover-paper': {
          overflow: 'hidden',
          borderRadius: 2,
          marginTop: -3,

        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'transparent'
        }
      }}
    >
      <EscrowContract />
    </Popover>
  );
}

SolanaContractDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  anchorEl: PropTypes.object
};

export default withTranslation()(SolanaContractDialog);
